// src/features/auth/components/LoginForm.tsx

import React, { useState } from "react";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LoginCredentials } from "../types";

interface LoginFormProps {
  onSubmit: (credentials: LoginCredentials) => void;
  isLoading: boolean;
  error: string | null;
}

export const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({ email: "", password: "" });

  const validate = (): boolean => {
    let isValid = true;
    const newErrors = { email: "", password: "" };

    if (!email) {
      newErrors.email = "กรุณากรอกอีเมล";
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "รูปแบบอีเมลไม่ถูกต้อง";
      isValid = false;
    }

    if (!password) {
      newErrors.password = "กรุณากรอกรหัสผ่าน";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (validate()) {
      onSubmit({ email, password });
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Box sx={{ position: "relative", width: "70%", mt: 1, mb: 2 }}>
          {error && (
            <Typography
              variant="caption"
              color="error"
              sx={{ position: "absolute", top: 0, right: 0 }}
            >
              {error}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email || !!error}
            helperText={errors.email}
            disabled={isLoading}
            margin="normal"
            autoComplete="email"
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
          />
        </Box>

        <TextField
          label="Password"
          type={showPassword ? "text" : "password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!errors.password || !!error}
          helperText={errors.password}
          disabled={isLoading}
          margin="normal"
          autoComplete="current-password"
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            width: "70%",
            mb: 1,
            "& .MuiOutlinedInput-root": { borderRadius: "10px" },
          }}
        />
      </Box>

      <div style={{ width: "100%", textAlign: "center" }}>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isLoading || !email || !password}
          sx={{
            mb: 2,
            mt: 4,
            bgcolor: "#000",
            color: "#fff",
            "&:hover": {
              bgcolor: "#333",
            },
            textTransform: "none",
            py: 1.5,
            width: "200px",
          }}
        >
          {isLoading ? "Login..." : "Login"}
        </Button>
      </div>

      <Typography align="center" sx={{ fontSize: 16, color: "#000", textDecoration: "underline" }}>
        <a
          href="forgot-password"
          style={{ color: "#000", textDecoration: "none" }}
        >
          Forgot password
        </a>
      </Typography>
    </Box>
  );
};
